<script setup lang="ts">
import type { Component } from 'vue'
import { VIcon } from '@/modules/shared/components'

withDefaults(
  defineProps<{
    disabled?: boolean
    tooltip?: string
    href?: string | null
    icon?: string
    iconComponent?: Component
    iconLeft?: boolean
    loading?: boolean
    tag?: string
    target?: string
    text: string
    to?: any
    // Marks the item as the current value (e.g. the selected stage in a
    // pill menu): renders a leading status dot in dotColor.
    selected?: boolean
    dotColor?: string
  }>(),
  {
    disabled: false,
    tooltip: '',
    href: null,
    iconLeft: false,
    loading: false,
    tag: 'button',
    target: '_self',
    selected: false,
    dotColor: '#3b88af',
  },
)
</script>

<template>
  <component
    class="block w-full text-left"
    :disabled="disabled"
    :href="href"
    :is="tag"
    :target="target"
    :title="tooltip || undefined"
    :to="to"
    :aria-current="selected ? 'true' : undefined"
  >
    <label
      class="group flex w-full items-center justify-between space-x-2 px-4 py-2 text-sm font-medium"
      :class="{
        'cursor-pointer text-gray-700 hover:bg-gray-100': !disabled,
        'cursor-not-allowed bg-gray-50 text-gray-400': disabled,
      }"
      :title="tooltip"
    >
      <div v-if="!loading" class="flex w-full items-center gap-2" :class="{ 'justify-between': !iconLeft }">
        <span :class="iconLeft ? 'order-first' : 'order-last'">
          <VIcon
            v-if="icon"
            :name="icon"
            class="h-4 w-4 text-gray-500 group-hover:text-gray-600 group-[.cursor-not-allowed]:text-gray-300"
          />
          <component
            v-else-if="iconComponent"
            :is="iconComponent"
            class="h-4 w-4 text-gray-500 group-hover:text-gray-600 group-[.cursor-not-allowed]:text-gray-300"
          />
        </span>
        <span class="flex items-center gap-2">
          <span
            v-if="selected"
            class="h-1.5 w-1.5 flex-shrink-0 rounded-full"
            :style="{ backgroundColor: dotColor }"
          ></span>
          <span>{{ text }}</span>
        </span>
      </div>
      <div v-else class="mx-auto w-fit">
        <VIcon name="Loading" class="h-4 w-4 animate-spin-fast" />
      </div>
      <slot />
    </label>
  </component>
</template>
